import React, { useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { ContentTypeConstants } from '../../constants/contentCreationConstants';
import { apiDelete } from '../../utils/apiAuth';
import { Button, StatusBadge } from '../ui';

const Header = ({
  navigate,
  sub_name,
  contentType,
  openModal,
  approvalStatus,
  onApprovalStatusChange,
  batchAssigned,
  containerStatus,
  onContainerDeleted,
  isReadOnly = false,
  isSaveLoading = false,
}) => {
  const [isChangingStatus, setIsChangingStatus] = useState(false);
  const [isDeletingContainer, setIsDeletingContainer] = useState(false);

  const userType = sessionStorage.getItem('Type') || '';
  const isAdmin = userType.includes('AD');

  const getTitle = () => {
    if (contentType === ContentTypeConstants.PROJECT_CODING.key) {
      try {
        const projectName = sessionStorage.getItem('contentCreation_projectName');
        const parsed = projectName ? JSON.parse(projectName) : null;
        if (parsed) return parsed;
      } catch {
        // ignore
      }
    }
    return `${sub_name || 'Content'} creation`;
  };

  const handleBack = () => {
    const goToProjectContent = () => {
      const raw =
        sessionStorage.getItem('contentCreation_projectId') ||
        sessionStorage.getItem('projectId');
      if (raw) {
        try {
          const pid = JSON.parse(raw);
          navigate('/project-content', { state: { projectId: pid } });
          return;
        } catch {
          // fall through
        }
      }
      navigate('/project-content');
    };

    try {
      const navSource = sessionStorage.getItem('contentCreation_navSource');

      // Explicit source always wins — never infer from content type alone
      // (subject Content Bank can also open project_coding items).
      if (navSource === 'content-bank') {
        navigate('/content-bank');
        return;
      }
      if (navSource === 'testing') {
        const testingReturnPath = sessionStorage.getItem(
          'contentCreation_testingReturnPath'
        );
        navigate(
          testingReturnPath && testingReturnPath.startsWith('/testing/')
            ? testingReturnPath
            : '/dashboard'
        );
        return;
      }
      if (navSource === 'project') {
        goToProjectContent();
        return;
      }

      // Fallbacks when navSource was not set
      if (sessionStorage.getItem('contentBank_subjectId')) {
        navigate('/content-bank');
        return;
      }
      if (
        sessionStorage.getItem('contentCreation_freezeFilters') === 'true' ||
        sessionStorage.getItem('contentCreation_projectId')
      ) {
        goToProjectContent();
        return;
      }
      if (sessionStorage.getItem('projectId')) {
        goToProjectContent();
        return;
      }
    } catch {
      // ignore
    }
    navigate('/dashboard');
  };

  const getApprovalAction = () => {
    if (!approvalStatus) return null;

    const status = String(approvalStatus).toLowerCase();
    switch (status) {
      case 'uploaded':
        return isAdmin
          ? { label: 'Approve', nextStatus: 'approved', color: 'success' }
          : { label: 'Uploaded', disabled: true };
      case 'approved':
        return isAdmin
          ? { label: 'Unapprove', nextStatus: 'uploaded', color: 'warning' }
          : { label: 'Approved', disabled: true };
      case 'assigned':
        return { label: 'Assigned', disabled: true };
      case 'selected':
        return { label: 'Selected', disabled: true };
      default:
        return { label: status, disabled: true };
    }
  };

  const approvalAction = getApprovalAction();

  const handleStatusButtonClick = async () => {
    if (!onApprovalStatusChange || !approvalAction?.nextStatus) return;
    setIsChangingStatus(true);
    try {
      await onApprovalStatusChange(approvalAction.nextStatus);
    } finally {
      setIsChangingStatus(false);
    }
  };

  const handleDeleteContainer = async () => {
    const studentId = containerStatus?.student_id || containerStatus?.studentId;
    if (!studentId || !onContainerDeleted) return;
    setIsDeletingContainer(true);
    try {
      await apiDelete(`/api/student/vscode/${studentId}`);
      onContainerDeleted();
    } catch {
      // ignore
    } finally {
      setIsDeletingContainer(false);
    }
  };

  const showDeleteContainer =
    contentType === ContentTypeConstants.PROJECT_CODING.key &&
    containerStatus &&
    (containerStatus.student_id || containerStatus.studentId);

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        bgcolor: 'background.paper',
        borderBottom: '1px solid var(--color-border-default)',
        boxShadow: 'var(--shadow-xs)',
        px: 2,
        py: 1.25,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Button
            variant="ghost"
            size="small"
            startIcon={<ArrowBackIcon fontSize="small" />}
            onClick={handleBack}
          >
            Back
          </Button>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {getTitle()}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>
              {contentType?.replace(/_/g, ' ') || 'Content'}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          {approvalStatus && (
            <StatusBadge status={String(approvalStatus).toLowerCase()} />
          )}

          {approvalAction && approvalAction.nextStatus && isAdmin && (
            <Button
              variant="outlined"
              size="small"
              disabled={isChangingStatus || batchAssigned}
              loading={isChangingStatus}
              onClick={handleStatusButtonClick}
              title={batchAssigned ? 'Content is locked — project is batch-assigned' : ''}
            >
              {approvalAction.label}
            </Button>
          )}

          {showDeleteContainer && (
            <Button
              variant="danger-outlined"
              size="small"
              disabled={isDeletingContainer}
              loading={isDeletingContainer}
              onClick={handleDeleteContainer}
            >
              Delete container
            </Button>
          )}

          <Button
            variant="primary"
            size="small"
            onClick={openModal}
            disabled={isReadOnly || isSaveLoading}
            loading={isSaveLoading}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default Header;
